import { useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../hooks/useApiClient';
import type { UdpLogsResponse } from '@metron/shared';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toErrorMessage } from '@metron/shared';

// Frame type colors
const FRAME_TYPE_COLORS: Record<string, string> = {
  '02': '#3b82f6', // blue
  '03': '#10b981', // green
  'A2': '#f59e0b', // amber
  'A3': '#ef4444', // red
  'A0': '#8b5cf6', // purple
  'unknown': '#6b7280', // gray
};

export function UdpLogsPage() {
  const apiClient = useApiClient();
  const [data, setData] = useState<UdpLogsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.get<UdpLogsResponse>('udp-logs');
        // Reverse arrays to show oldest first (for charts)
        setData({
          ...response,
          messagesPerBucket: response.messagesPerBucket.reverse(),
          validationRate: response.validationRate.reverse(),
          serverLatency: response.serverLatency.reverse(),
          frameTypes: response.frameTypes.reverse(),
          direction: response.direction.reverse(),
          activeDevices: response.activeDevices.reverse(),
        });
      } catch (err) {
        setError(toErrorMessage(err, 'Failed to fetch UDP logs data'));
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [apiClient]);

  // Format time for display
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Transform messages per bucket data for chart
  const messagesPerBucketData = useMemo(() => {
    if (!data) return [];
    return data.messagesPerBucket.map((point) => ({
      time: formatTime(point.bucket),
      count: point.messageCount,
      bucket: point.bucket,
    }));
  }, [data]);

  // Transform validation rate data for chart
  const validationRateData = useMemo(() => {
    if (!data) return [];
    return data.validationRate.map((point) => ({
      time: formatTime(point.bucket),
      rate: point.validationRate,
      bucket: point.bucket,
    }));
  }, [data]);

  // Transform server latency data for chart
  const serverLatencyData = useMemo(() => {
    if (!data) return [];
    return data.serverLatency.map((point) => ({
      time: formatTime(point.bucket),
      latency: point.avgLatencySeconds,
      pairs: point.matchedPairs,
      bucket: point.bucket,
    }));
  }, [data]);

  // Transform frame types data for stacked area chart
  const frameTypesData = useMemo(() => {
    if (!data) return [];

    // Group by bucket and pivot frame types
    const bucketMap = new Map<string, Record<string, number>>();

    data.frameTypes.forEach((point) => {
      const bucket = point.bucket;
      if (!bucketMap.has(bucket)) {
        bucketMap.set(bucket, { time: formatTime(bucket), bucket });
      }
      const bucketData = bucketMap.get(bucket)!;
      bucketData[point.frameType] = point.count;
    });

    return Array.from(bucketMap.values());
  }, [data]);

  // Transform direction data for stacked area chart
  const directionData = useMemo(() => {
    if (!data) return [];
    return data.direction.map((point) => ({
      time: formatTime(point.bucket),
      Inbound: point.inboundCount,
      Outbound: point.outboundCount,
      bucket: point.bucket,
    }));
  }, [data]);

  // Transform active devices data for chart
  const activeDevicesData = useMemo(() => {
    if (!data) return [];
    return data.activeDevices.map((point) => ({
      time: formatTime(point.bucket),
      devices: point.deviceCount,
      bucket: point.bucket,
    }));
  }, [data]);

  // Get unique frame types for legend
  const frameTypes = useMemo(() => {
    if (!data) return [];
    const types = new Set<string>();
    data.frameTypes.forEach((point) => types.add(point.frameType));
    return Array.from(types).sort();
  }, [data]);

  if (loading) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Wave UDP Logs</h1>
        <div className='flex items-center justify-center h-64'>
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Wave UDP Logs</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      <div className='flex items-center justify-between'>
        <h1 className='text-3xl font-bold'>Wave UDP Logs</h1>
        <p className='text-muted-foreground'>Last 24 hours at 5-minute resolution</p>
      </div>

      {/* Summary Statistics */}
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4'>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Total Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{summary.totalMessages.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Valid Inbound
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                summary.validInbound === summary.validOutbound ? 'text-green-600' : ''
              }`}
            >
              {summary.validInbound.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Valid Outbound
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                summary.validInbound === summary.validOutbound ? 'text-green-600' : ''
              }`}
            >
              {summary.validOutbound.toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              No Server Response
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                summary.noResponseRate >= 0 && summary.noResponseRate <= 1
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {summary.noResponseRate > 0 && summary.noResponseRate < 0.05 ? '~' : ''}
              {summary.noResponseRate.toFixed(1)}%
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Validated Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{summary.validationRate.toFixed(1)}%</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Unique Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{summary.uniqueDevices.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Unique Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>{summary.uniqueAccounts.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Chart 1: UDP Messages Per Time Bucket */}
        <Card>
          <CardHeader>
            <CardTitle>UDP Messages Per Time Bucket</CardTitle>
            <CardDescription>Total message volume over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={messagesPerBucketData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis />
                <Tooltip />
                <Line
                  type='monotone'
                  dataKey='count'
                  stroke='#3b82f6'
                  strokeWidth={2}
                  dot={false}
                  name='Messages'
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 2: Valid Frame Precentage*/}
        <Card>
          <CardHeader>
            <CardTitle>Valid Frame Precentage</CardTitle>
            <CardDescription>Percentage of valid frames over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={validationRateData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line
                  type='monotone'
                  dataKey='rate'
                  stroke='#10b981'
                  strokeWidth={2}
                  dot={false}
                  name='Validation Rate (%)'
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 3: Server Latency */}
        <Card>
          <CardHeader>
            <CardTitle>Server Response Latency</CardTitle>
            <CardDescription>
              Average response time (inbound to outbound) in seconds
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={serverLatencyData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis />
                <Tooltip />
                <Line
                  type='monotone'
                  dataKey='latency'
                  stroke='#f59e0b'
                  strokeWidth={2}
                  dot={false}
                  name='Latency (seconds)'
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 4: Frame Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Inbound Frame Types Distribution</CardTitle>
            <CardDescription>Message composition by frame type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <AreaChart data={frameTypesData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis />
                <Tooltip />
                <Legend />
                {frameTypes.map((frameType) => (
                  <Area
                    key={frameType}
                    type='monotone'
                    dataKey={frameType}
                    stackId='1'
                    stroke={FRAME_TYPE_COLORS[frameType] || '#6b7280'}
                    fill={FRAME_TYPE_COLORS[frameType] || '#6b7280'}
                    name={`Frame ${frameType}`}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 5: Inbound vs Outbound Messages */}
        <Card>
          <CardHeader>
            <CardTitle>Inbound vs Outbound Messages</CardTitle>
            <CardDescription>Message direction over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <AreaChart data={directionData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type='monotone'
                  dataKey='Inbound'
                  stackId='1'
                  stroke='#3b82f6'
                  fill='#3b82f6'
                  name='Inbound'
                />
                <Area
                  type='monotone'
                  dataKey='Outbound'
                  stackId='1'
                  stroke='#ef4444'
                  fill='#ef4444'
                  name='Outbound'
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Chart 6: Active Devices Per Time Bucket */}
        <Card>
          <CardHeader>
            <CardTitle>Active Devices Per Time Bucket</CardTitle>
            <CardDescription>Unique devices communicating over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width='100%' height={300}>
              <LineChart data={activeDevicesData}>
                <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                <XAxis dataKey='time' tick={{ fontSize: 11 }} interval={47} />
                <YAxis />
                <Tooltip />
                <Line
                  type='monotone'
                  dataKey='devices'
                  stroke='#8b5cf6'
                  strokeWidth={2}
                  dot={false}
                  name='Active Devices'
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
